/**
 * Botones de descarga de los documentos de entrega de un lote liberado.
 * Desde 2026-09-10 puede haber uno por correo (GET /api/warehouse/batch/:id/releases);
 * si no hay filas (lotes históricos), cae al PDF único `packing_release_url`.
 */
import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import type { WhCourier } from '../shared';

interface ReleaseRow {
  id: number;
  courier_id: number | null;
  pdf_url: string;
  orders_count: number;
  items_count: number;
  courier: Pick<WhCourier, 'id' | 'key' | 'name' | 'color'> | null;
}

export function ReleasePdfButtons({
  batchId,
  sellerId,
  fallbackUrl,
  size = 'sm',
}: {
  batchId: number;
  /** vendedor dueño del lote: el servidor lo valida (la mesa ve varios vendedores) */
  sellerId: number;
  fallbackUrl: string | null;
  size?: 'sm' | 'xs';
}) {
  const [rows, setRows] = useState<ReleaseRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`/api/warehouse/batch/${batchId}/releases?seller_id=${sellerId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { releases: [] }))
      .then((d) => alive && setRows(Array.isArray(d.releases) ? d.releases : []))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [batchId]);

  const pad = size === 'xs' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-xs';
  const open = (url: string) => window.open(`/api/warehouse/labels/${url}`, '_blank');

  if (rows && rows.length > 0) {
    return (
      <>
        {rows.map((r) => (
          <button
            key={r.id}
            onClick={() => open(r.pdf_url)}
            className={`${pad} inline-flex items-center gap-1 rounded-md font-medium text-white hover:opacity-90`}
            style={{ backgroundColor: r.courier?.color ?? '#7c3aed' }}
            title={r.courier ? `Documento de entrega ${r.courier.name}` : 'Documento de entrega'}
          >
            <Download size={12} /> PDF{r.courier ? ` ${r.courier.name}` : ''}
          </button>
        ))}
      </>
    );
  }
  if (!fallbackUrl) return null;
  return (
    <button
      onClick={() => open(fallbackUrl)}
      className={`${pad} inline-flex items-center gap-1 rounded-md bg-purple-600 font-medium text-white hover:bg-purple-700`}
    >
      <Download size={12} /> PDF
    </button>
  );
}
